import React, { useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  Check,
  CheckCircle2,
  Clock3,
  Loader2,
  LockKeyhole,
  Phone,
  ShieldCheck,
  Sparkles,
  Star,
  UtensilsCrossed,
} from "lucide-react";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { useGlobalContext } from "../Context/GlobalContext";
import { useProfile } from "../Services/Profile/useProfile";
import { useLogin } from "../../Components/Services/useLogin";
import ChaafoLogo from "../Images/Chaafo_Logo.png";

const normalizeLoginResponse = (response) => {
  const responseValue = response?.response;

  const user = Array.isArray(responseValue)
    ? responseValue[0] || {}
    : responseValue || {};

  const userCode = String(
    user?.user_code ||
      user?.userCode ||
      user?.userid ||
      user?.userId ||
      user?.customer_code ||
      "",
  ).trim();

  const firstName = String(user?.first_name || user?.firstName || "").trim();

  const lastName = String(user?.last_name || user?.lastName || "").trim();

  const privacyPolicy = String(
    user?.privacy_policy || user?.privacyPolicy || "N",
  ).toUpperCase();

  const profileCompleted =
    Boolean(firstName) && Boolean(lastName) && privacyPolicy === "Y";

  return {
    user,
    userCode,
    firstName,
    lastName,
    privacyPolicy,
    profileCompleted,
  };
};

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { setUser } = useGlobalContext();

  const { loginGateway, loading, error, setError } = useLogin();
const { getProfileDetails } = useProfile();
  const [currentStep, setCurrentStep] = useState(1);
  const [number, setNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [resending, setResending] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);
const otpInputRefs = useRef([]);

  const getRequestedRoute = () => {
    const from = location.state?.from;

    if (!from?.pathname) {
      return "/";
    }

    return `${from.pathname || ""}${from.search || ""}${from.hash || ""}`;
  };

  useEffect(() => {
    if (resendSeconds <= 0) return undefined;

    const timer = window.setInterval(() => {
      setResendSeconds((previous) => {
        if (previous <= 1) {
          window.clearInterval(timer);
          return 0;
        }

        return previous - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [resendSeconds]);

  const clearMessages = () => {
    setError("");
    setSuccessMessage("");
  };

const handleNumberChange = (event) => {
  let value = event.target.value.replace(/\D/g, "");

  // If Chrome autofills +91XXXXXXXXXX, remove the country code
  if (value.length > 10 && value.startsWith("91")) {
    value = value.slice(2);
  }

  // Keep only the last 10 digits
  value = value.slice(-10);

  setNumber(value);
  clearMessages();
};

 const handleOtpBoxChange = (index, event) => {
   const digit = event.target.value.replace(/\D/g, "").slice(-1);

   const otpArray = otp.padEnd(6, " ").split("");

   otpArray[index] = digit;

   const updatedOtp = otpArray.join("").replace(/\s/g, "").slice(0, 6);

   setOtp(updatedOtp);
   clearMessages();

   if (digit && index < 5) {
     otpInputRefs.current[index + 1]?.focus();
   }
 };

 const handleOtpKeyDown = (index, event) => {
   if (event.key === "Backspace") {
     const otpArray = otp.padEnd(6, " ").split("");

     if (!otpArray[index]?.trim() && index > 0) {
       otpInputRefs.current[index - 1]?.focus();
       return;
     }

     otpArray[index] = "";

     setOtp(otpArray.join("").replace(/\s/g, ""));
     clearMessages();
   }

   if (event.key === "ArrowLeft" && index > 0) {
     otpInputRefs.current[index - 1]?.focus();
   }

   if (event.key === "ArrowRight" && index < 5) {
     otpInputRefs.current[index + 1]?.focus();
   }
 };

 const handleOtpPaste = (event) => {
   event.preventDefault();

   const pastedOtp = event.clipboardData
     .getData("text")
     .replace(/\D/g, "")
     .slice(0, 6);

   if (!pastedOtp) {
     return;
   }

   setOtp(pastedOtp);
   clearMessages();

   const nextIndex = Math.min(pastedOtp.length, 5);

   window.requestAnimationFrame(() => {
     otpInputRefs.current[nextIndex]?.focus();
   });
 };

  const handleSendOtp = async (event) => {
    event.preventDefault();
    clearMessages();

    if (!/^[6-9]\d{9}$/.test(number)) {
      setError("Please enter a valid 10-digit mobile number.");
      return;
    }

    try {
      const response = await loginGateway({
        number,
        otp: "",
        step: "1",
      });

      if (String(response?.responseCode) !== "00") {
        setError(response?.responseMessage || "Unable to generate OTP.");
        return;
      }

      setCurrentStep(2);
      setOtp("");
      setResendSeconds(30);
window.requestAnimationFrame(() => {
  otpInputRefs.current[0]?.focus();
});
      setSuccessMessage(
        response?.responseMessage || "OTP generated successfully.",
      );
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const handleVerifyOtp = async (event) => {
    event.preventDefault();
    clearMessages();

   if (!/^\d{6}$/.test(otp)) {
     setError("Please enter the complete 6-digit OTP.");
     return;
   }

    try {
      const response = await loginGateway({
        number,
        otp,
        step: "2",
      });

      if (String(response?.responseCode) !== "00") {
        toast.error("OTP is Invalid");
        return;
      }

      const {
        user: apiUser,
        userCode,
        firstName,
        lastName,
        privacyPolicy,
        profileCompleted,
      } = normalizeLoginResponse(response);

      if (!userCode) {
        setError("User code was not received from login API.");
        return;
      }

 let loggedInUser = {
   ...apiUser,
   user_code: userCode,
   first_name: firstName,
   last_name: lastName,
   privacy_policy: privacyPolicy,
 };

 // =====================================================
 // GET PROFILE DETAILS - ONLY ONCE AFTER SUCCESSFUL LOGIN
 // =====================================================

 try {
   const profileResult = await getProfileDetails({
     userCode,
   });

   if (profileResult?.success && profileResult?.data) {
loggedInUser = {
  ...loggedInUser,
  ...profileResult.data,

  user_code:
    profileResult.data?.user_code || profileResult.data?.userCode || userCode,

  phone: profileResult.data?.phone || profileResult.data?.number || number,
};
   }
 } catch (profileError) {
   console.error("Unable to load profile after login:", profileError);
 }

 delete loggedInUser.number;

 // This automatically saves into Context + sessionStorage customer
 setUser(loggedInUser);

   // Keep only complete customer object
   sessionStorage.setItem("customer", JSON.stringify(loggedInUser));

   const requestedRoute = getRequestedRoute();

      if (!profileCompleted) {
        navigate("/profile-details", {
          replace: true,
          state: {
            userCode,
            number,
            user: loggedInUser,
            from: location.state?.from || null,
          },
        });

        return;
      }

      navigate(requestedRoute, {
        replace: true,
      });
    } catch (requestError) {
      toast.error("OTP is Invalid");
    }
  };

  const handleResendOtp = async () => {
    if (resendSeconds > 0 || resending) return;

    clearMessages();

    try {
      setResending(true);

      const response = await loginGateway({
        number,
        otp: "",
        step: "1",
      });

      if (String(response?.responseCode) !== "00") {
        setError(response?.responseMessage || "Unable to resend OTP.");
        return;
      }

      setOtp("");
      setResendSeconds(30);

      setSuccessMessage(
        response?.responseMessage || "OTP sent again successfully.",
      );
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setResending(false);
    }
  };

  const handleChangeNumber = () => {
    setCurrentStep(1);
    setOtp("");
    setResendSeconds(0);
    clearMessages();
  };

  const maskedNumber = number
    ? `${number.slice(0, 2)}******${number.slice(-2)}`
    : "";

    useEffect(() => {
      if (currentStep !== 2) {
        return;
      }

      const focusTimer = window.setTimeout(() => {
        otpInputRefs.current[0]?.focus();
      }, 100);

      return () => window.clearTimeout(focusTimer);
    }, [currentStep]);

  return (
    <main className="relative h-[100dvh] overflow-hidden bg-[#f5f3ef] font-[Inter]">
      {/* Background */}
      <div className="pointer-events-none absolute -left-28 -top-28 h-64 w-64 rounded-full bg-[#ff6b35]/10 blur-3xl sm:h-80 sm:w-80" />

      <div className="pointer-events-none absolute -bottom-32 -right-28 h-72 w-72 rounded-full bg-[#111827]/10 blur-3xl sm:h-[420px] sm:w-[420px]" />

      <div className="relative mx-auto flex min-h-[100dvh] w-full max-w-[1500px] items-stretch justify-center sm:items-center sm:p-5 lg:p-8">
        <div className="grid min-h-[100dvh] w-full max-w-[1240px] overflow-hidden bg-white sm:min-h-0 sm:rounded-[28px] sm:border sm:border-white/70 sm:shadow-[0_35px_100px_rgba(15,23,42,0.14)] lg:min-h-[720px] lg:grid-cols-[1.05fr_0.95fr]">
          {/* Desktop left panel */}
          <section className="relative hidden overflow-hidden bg-[#111827] p-10 text-white lg:flex lg:flex-col lg:justify-between xl:p-14">
            <div className="absolute inset-0">
              <div className="absolute -left-28 -top-24 h-80 w-80 rounded-full bg-[#ff6b35]/25 blur-3xl" />

              <div className="absolute -bottom-36 -right-24 h-[460px] w-[460px] rounded-full bg-[#ffb703]/10 blur-3xl" />

              <div
                className="absolute inset-0 opacity-[0.06]"
                style={{
                  backgroundImage:
                    "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
                  backgroundSize: "24px 24px",
                }}
              />
            </div>

            <div className="relative z-10">
              <button
                type="button"
                onClick={() => navigate("/")}
                className="flex items-center gap-3"
              >
                <span className="flex h-[74px] w-[74px] items-center justify-center rounded-[22px] border border-white/20 bg-white shadow-xl">
                  <img
                    src={ChaafoLogo}
                    alt="Chaafo Logo"
                    className="h-[58px] w-[58px] object-contain"
                  />
                </span>

                <div className="text-left">
                  <p className="text-[22px] font-extrabold tracking-tight">
                    Chaafo
                  </p>

                  <p className="text-xs text-white/55">
                    Food, delivered better
                  </p>
                </div>
              </button>

              <div className="mt-16">
                <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-semibold text-white/80 backdrop-blur-xl">
                  <Sparkles size={15} className="text-[#ffb703]" />
                  Premium food ordering experience
                </div>

                <h1 className="mt-7 max-w-[520px] text-[48px] font-extrabold leading-[1.08] tracking-[-0.04em] xl:text-[56px]">
                  Great food.
                  <span className="block text-[#ff8a4c]">
                    Effortless ordering.
                  </span>
                </h1>

                <p className="mt-6 max-w-[500px] text-[16px] leading-8 text-white/65">
                  Sign in securely and discover curated restaurants, exclusive
                  offers and faster doorstep delivery.
                </p>
              </div>

              <div className="mt-10 grid grid-cols-3 gap-3">
                <div className="rounded-[20px] border border-white/10 bg-white/[0.07] p-4 backdrop-blur-xl">
                  <UtensilsCrossed size={22} className="text-[#ff8a4c]" />

                  <p className="mt-4 text-lg font-bold">500+</p>

                  <p className="mt-1 text-xs text-white/50">Food choices</p>
                </div>

                <div className="rounded-[20px] border border-white/10 bg-white/[0.07] p-4 backdrop-blur-xl">
                  <Clock3 size={22} className="text-[#ff8a4c]" />

                  <p className="mt-4 text-lg font-bold">Fast</p>

                  <p className="mt-1 text-xs text-white/50">Delivery</p>
                </div>

                <div className="rounded-[20px] border border-white/10 bg-white/[0.07] p-4 backdrop-blur-xl">
                  <BadgeCheck size={22} className="text-[#ff8a4c]" />

                  <p className="mt-4 text-lg font-bold">Secure</p>

                  <p className="mt-1 text-xs text-white/50">OTP login</p>
                </div>
              </div>
            </div>

            <div className="relative z-10 mt-10 flex items-center justify-between rounded-[22px] border border-white/10 bg-white/[0.07] p-5 backdrop-blur-xl">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#ff6b35]/20">
                  <Star
                    size={20}
                    fill="currentColor"
                    className="text-[#ffb703]"
                  />
                </div>

                <div>
                  <p className="text-sm font-semibold">Loved by food lovers</p>

                  <p className="mt-1 text-xs text-white/50">
                    Simple and secure ordering
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Login section */}
          <section className="relative flex min-h-[100dvh] w-full items-start justify-center overflow-y-auto px-4 pb-8 pt-[76px] min-[375px]:px-5 sm:min-h-[670px] sm:items-center sm:px-10 sm:pt-16 lg:min-h-0 lg:px-14 xl:px-20">
            {/* Back button */}
            <button
              type="button"
              onClick={() => navigate("/")}
              className="absolute left-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-[#e5e7eb] bg-white text-[#374151] shadow-sm transition hover:bg-[#f8fafc] sm:left-7 sm:top-7 sm:h-11 sm:w-11"
              aria-label="Back to home"
            >
              <ArrowLeft size={19} />
            </button>

            <div className="w-full max-w-[450px]">
              {/* Mobile logo */}
              <div className="mb-5 flex flex-col items-center sm:mb-7 lg:hidden">
                <div className="flex h-[68px] w-[68px] items-center justify-center rounded-[19px] border border-[#ece7df] bg-white shadow-[0_12px_30px_rgba(15,23,42,0.09)] min-[375px]:h-[76px] min-[375px]:w-[76px] sm:h-[86px] sm:w-[86px] sm:rounded-[24px]">
                  <img
                    src={ChaafoLogo}
                    alt="Chaafo Logo"
                    className="h-[52px] w-[52px] object-contain min-[375px]:h-[60px] min-[375px]:w-[60px] sm:h-[68px] sm:w-[68px]"
                  />
                </div>

                <p className="mt-2 text-lg font-extrabold text-[#111827] sm:mt-3 sm:text-xl">
                  Chaafo
                </p>
              </div>

              {/* Step progress */}
              <div className="mb-5 flex items-center gap-2.5 sm:mb-8 sm:gap-3">
                <div
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold transition sm:h-9 sm:w-9 sm:text-sm ${
                    currentStep >= 1
                      ? "bg-[#111827] text-white"
                      : "bg-[#f3f4f6] text-[#9ca3af]"
                  }`}
                >
                  {currentStep > 1 ? <Check size={15} /> : "1"}
                </div>

                <div className="h-[2px] min-w-0 flex-1 overflow-hidden rounded-full bg-[#eceff3]">
                  <div
                    className={`h-full rounded-full bg-[#ff6b35] transition-all duration-500 ${
                      currentStep === 2 ? "w-full" : "w-0"
                    }`}
                  />
                </div>

                <div
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold transition sm:h-9 sm:w-9 sm:text-sm ${
                    currentStep === 2
                      ? "bg-[#ff6b35] text-white"
                      : "bg-[#f3f4f6] text-[#9ca3af]"
                  }`}
                >
                  2
                </div>
              </div>

              {/* Heading */}
              <div className="mb-6 sm:mb-8">
                <div className="inline-flex items-center gap-1.5 rounded-full bg-[#fff3ec] px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-[#ef5b2a] min-[375px]:text-[11px] sm:gap-2 sm:px-3 sm:text-xs sm:tracking-[0.12em]">
                  <ShieldCheck size={13} />
                  Secure sign in
                </div>

                <h2 className="mt-4 break-words text-[28px] font-extrabold leading-[1.12] tracking-[-0.03em] text-[#111827] min-[375px]:text-[31px] sm:mt-3 sm:text-[40px]">
                  {currentStep === 1 ? "Welcome back" : "Verify your identity"}
                </h2>

                <p className="mt-3 text-[13px] leading-6 text-[#6b7280] min-[375px]:text-[14px] sm:mt-4 sm:text-[15px] sm:leading-7">
                  {currentStep === 1
                    ? "Enter your mobile number to continue with your Chaafo account."
                    : `We sent a verification code to +91 ${maskedNumber}.`}
                </p>
              </div>

              {currentStep === 1 ? (
                <form onSubmit={handleSendOtp}>
                  <label
                    htmlFor="mobile-number"
                    className="mb-2 block text-[13px] font-bold text-[#374151] sm:mb-2.5 sm:text-sm"
                  >
                    Mobile number
                  </label>

                  <div className="group flex h-[54px] min-w-0 overflow-hidden rounded-[14px] border border-[#dfe3e8] bg-white shadow-[0_6px_20px_rgba(15,23,42,0.04)] transition focus-within:border-[#ff6b35] focus-within:ring-4 focus-within:ring-[#ff6b35]/10 sm:h-[62px] sm:rounded-[16px]">
                    <div className="flex shrink-0 items-center gap-1.5 border-r border-[#e7e9ed] bg-[#f8f9fb] px-2.5 font-bold text-[#374151] min-[375px]:px-3 sm:gap-2 sm:px-4">
                      <span className="text-base sm:text-lg">🇮🇳</span>

                      <span className="text-sm sm:text-base">+91</span>
                    </div>

                    <div className="flex min-w-0 flex-1 items-center bg-white">
                      <Phone
                        size={17}
                        className="ml-2.5 hidden shrink-0 text-[#9ca3af] transition group-focus-within:text-[#ff6b35] min-[350px]:block sm:ml-4 sm:h-[19px] sm:w-[19px]"
                      />

                      <input
                        id="mobile-number"
                        type="tel"
                        inputMode="numeric"
                        autoComplete="tel"
                        value={number}
                        onChange={handleNumberChange}
                        placeholder="10-digit mobile number"
                        className="
    h-full
    min-w-0
    flex-1
    bg-white
    px-2.5
    text-[14px]
    font-medium
    text-[#111827]
    outline-none

    placeholder:font-normal
    placeholder:text-[#a2a8b1]

    [&:-webkit-autofill]:shadow-[inset_0_0_0_1000px_#ffffff]
    [&:-webkit-autofill]:[-webkit-text-fill-color:#111827]
    [&:-webkit-autofill]:caret-[#111827]

    min-[375px]:px-3
    min-[375px]:text-[15px]

    sm:text-[16px]
  "
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading || number.length !== 10}
                    className="group mt-5 flex h-[54px] w-full items-center justify-center gap-2.5 rounded-[14px] bg-[#111827] px-4 text-[14px] font-bold text-white shadow-[0_12px_30px_rgba(17,24,39,0.2)] transition hover:bg-[#202939] disabled:cursor-not-allowed disabled:opacity-50 sm:mt-6 sm:h-[58px] sm:rounded-[16px] sm:px-5 sm:text-[16px]"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={19} className="animate-spin" />
                        Sending OTP...
                      </>
                    ) : (
                      <>
                        Continue securely
                        <ArrowRight
                          size={19}
                          className="transition group-hover:translate-x-1"
                        />
                      </>
                    )}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleVerifyOtp}>
                  <div className="mb-2 flex items-center justify-between gap-3 sm:mb-2.5">
                    <label
                      htmlFor="otp"
                      className="text-[13px] font-bold text-[#374151] sm:text-sm"
                    >
                      Verification code
                    </label>

                    <button
                      type="button"
                      onClick={handleChangeNumber}
                      disabled={loading}
                      className="shrink-0 text-xs font-bold text-[#ef5b2a] transition hover:text-[#d94c1d] disabled:opacity-50 sm:text-sm"
                    >
                      Change number
                    </button>
                  </div>

                  <div className="rounded-[18px] border border-[#e5e7eb] bg-[#fafafa] p-3 shadow-[0_8px_24px_rgba(15,23,42,0.04)] min-[375px]:p-4 sm:rounded-[22px] sm:p-5">
                    <div className="mb-3 flex items-center gap-2 text-[11px] font-semibold text-[#6b7280] sm:mb-4 sm:text-xs">
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#fff0e9] text-[#ef5b2a]">
                        <LockKeyhole size={14} />
                      </span>
                      Enter the 6-digit verification code
                    </div>

                    <div
                      className="grid grid-cols-6 gap-1.5 min-[360px]:gap-2 sm:gap-3"
                      onPaste={handleOtpPaste}
                    >
                      {Array.from({ length: 6 }).map((_, index) => {
                        const digit = otp[index] || "";

                        return (
                          <input
                            key={index}
                            ref={(element) => {
                              otpInputRefs.current[index] = element;
                            }}
                            type="tel"
                            inputMode="numeric"
                            autoComplete={index === 0 ? "one-time-code" : "off"}
                            maxLength={1}
                            value={digit}
                            onChange={(event) =>
                              handleOtpBoxChange(index, event)
                            }
                            onKeyDown={(event) =>
                              handleOtpKeyDown(index, event)
                            }
                            onFocus={(event) => event.target.select()}
                            aria-label={`OTP digit ${index + 1}`}
                            className={`aspect-square min-w-0 rounded-[11px] border bg-white text-center text-[18px] font-extrabold text-[#111827] outline-none transition-all duration-200 min-[375px]:rounded-[13px] min-[375px]:text-[20px] sm:rounded-[15px] sm:text-[24px] ${
                              digit
                                ? "border-[#ff6b35] shadow-[0_6px_18px_rgba(255,107,53,0.12)] ring-2 ring-[#ff6b35]/10"
                                : "border-[#dfe3e8] hover:border-[#c9ced6] focus:border-[#ff6b35] focus:ring-4 focus:ring-[#ff6b35]/10"
                            }`}
                          />
                        );
                      })}
                    </div>

                    <div className="mt-3 flex items-center justify-between gap-3 text-[10px] text-[#9ca3af] sm:mt-4 sm:text-xs">
                      <span>{otp.length}/6 digits entered</span>

                      {otp.length === 6 && (
                        <span className="flex items-center gap-1 font-semibold text-[#15803d]">
                          <CheckCircle2 size={13} />
                          Code complete
                        </span>
                      )}
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading || otp.length !== 6}
                    className="group mt-3 flex h-[54px] w-full items-center justify-center gap-2.5 rounded-[14px] bg-[#111827] px-4 text-[14px] font-bold text-white shadow-[0_12px_30px_rgba(17,24,39,0.2)] transition hover:bg-[#202939] disabled:cursor-not-allowed disabled:opacity-50 sm:mt-6 sm:h-[58px] sm:rounded-[16px] sm:px-5 sm:text-[16px]"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={19} className="animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        Verify and continue
                        <ArrowRight
                          size={19}
                          className="transition group-hover:translate-x-1"
                        />
                      </>
                    )}
                  </button>

                  <div className="mt-4 flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 text-center">
                    <span className="text-xs text-[#6b7280] sm:text-sm">
                      Didn&apos;t receive the code?
                    </span>

                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={loading || resending || resendSeconds > 0}
                      className="text-xs font-bold text-[#ef5b2a] transition hover:text-[#d94c1d] disabled:cursor-not-allowed disabled:text-[#9ca3af] sm:text-sm"
                    >
                      {resending
                        ? "Sending..."
                        : resendSeconds > 0
                          ? `Resend in ${resendSeconds}s`
                          : "Resend OTP"}
                    </button>
                  </div>
                </form>
              )}

              {successMessage && (
                <div className="mt-4 flex items-start gap-2.5 rounded-[14px] border border-[#b8ebcf] bg-[#edfff4] px-3.5 py-3 text-xs font-semibold leading-5 text-[#157a44] sm:mt-5 sm:rounded-[16px] sm:px-4 sm:py-3.5 sm:text-sm">
                  <CheckCircle2
                    size={17}
                    className="mt-0.5 shrink-0 sm:h-[19px] sm:w-[19px]"
                  />

                  <span className="break-words">{successMessage}</span>
                </div>
              )}

              {error && (
                <div className="mt-4 break-words rounded-[14px] border border-[#fecaca] bg-[#fff1f1] px-3.5 py-3 text-xs font-semibold leading-5 text-[#c62828] sm:mt-5 sm:rounded-[16px] sm:px-4 sm:py-3.5 sm:text-sm">
                  {error}
                </div>
              )}

              <div className="mt-6 flex items-start justify-center gap-2 text-center text-[10px] leading-4 text-[#8a9099] sm:mt-4 sm:text-xs sm:leading-5">
                <ShieldCheck
                  size={14}
                  className="mt-0.5 shrink-0 sm:h-[15px] sm:w-[15px]"
                />

                <p>Your information is protected with secure authentication.</p>
              </div>

              <p className="mt-4 pb-[max(8px,env(safe-area-inset-bottom))] text-center text-[10px] leading-4 text-[#9ca3af] sm:mt-2 sm:text-xs sm:leading-5">
                By continuing, you agree to Chaafo&apos;s{" "}
                <button
                  type="button"
                  className="font-semibold text-[#4b5563] hover:underline"
                >
                  Terms of Service
                </button>{" "}
                and{" "}
                <button
                  type="button"
                  className="font-semibold text-[#4b5563] hover:underline"
                >
                  Privacy Policy
                </button>
                .
              </p>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
};

export default Login;
